import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const CreateMilestoneTemplateForm = ({ onSuccess }) => {
  const { tenant_id, loadingUser } = useContext(AuthContext);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    template_name: "",
    description: "",
    customer_id: "",
    is_default: false,
  });

  // Milestone items (dynamic list)
  const [items, setItems] = useState([
    { milestone_name: "", description: "", sequence_order: 1 },
  ]);

  // Load customers
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    secureAxios
      .get("/api/customers", { params: { tenant_id } })
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Error loading customers", err));
  }, [tenant_id, loadingUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      { milestone_name: "", description: "", sequence_order: items.length + 1 },
    ]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      alert("A template must have at least one milestone item.");
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    // Reorder sequence numbers
    newItems.forEach((item, i) => {
      item.sequence_order = i + 1;
    });
    setItems(newItems);
  };

  const moveItem = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === items.length - 1)
    ) {
      return;
    }

    const newItems = [...items];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    
    // Swap items
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    
    // Update sequence orders
    newItems.forEach((item, i) => {
      item.sequence_order = i + 1;
    });
    
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.template_name.trim()) {
      setError("Template name is required.");
      return;
    }

    const validItems = items.filter((item) => item.milestone_name.trim());
    if (validItems.length === 0) {
      setError("At least one milestone item with a name is required.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        tenant_id,
        customer_id: formData.customer_id || null,
        items: validItems.map((item, index) => ({
          milestone_name: item.milestone_name.trim(),
          description: item.description?.trim() || null,
          sequence_order: index + 1,
        })),
      };

      await secureAxios.post("/api/milestone-templates", payload);
      
      alert("Milestone template created successfully!");
      
      // Reset form
      setFormData({
        template_name: "",
        description: "",
        customer_id: "",
        is_default: false,
      });
      setItems([{ milestone_name: "", description: "", sequence_order: 1 }]);
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error creating milestone template:", err);
      setError(err.response?.data?.error || "Failed to create milestone template.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return <p>Loading...</p>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="mb-0">Create Milestone Template</h4>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Template Details */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">
                Template Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="template_name"
                value={formData.template_name}
                onChange={handleChange}
                placeholder="e.g., Standard 3-Unit Program"
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Customer (Optional)</label>
              <select
                className="form-select"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
              >
                <option value="">All Customers (Global Template)</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Leave blank to make this template available for all customers.
              </small>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                placeholder="Optional description of this template..."
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="is_default"
                name="is_default"
                checked={formData.is_default}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="is_default">
                Set as default template for this customer
              </label>
            </div>
          </div>

          <hr />

          {/* Milestone Items Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Milestone Items</h5>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={addItem}
              >
                + Add Milestone
              </button>
            </div>

            <p className="text-muted small mb-3">
              Define the individual milestones (billing units) for this template. 
              Each milestone will represent one billable flat rate charge.
            </p>

            {items.map((item, index) => (
              <div key={index} className="card mb-2 border-secondary">
                <div className="card-body py-2">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <div className="d-flex flex-column">
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() => moveItem(index, "up")}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <i className="bi bi-chevron-up"></i>
                          &uarr;
                        </button>
                        <span className="badge bg-secondary my-1">{item.sequence_order}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() => moveItem(index, "down")}
                          disabled={index === items.length - 1}
                          title="Move down"
                        >
                          <i className="bi bi-chevron-down"></i>
                          &darr;
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Milestone Name *"
                        value={item.milestone_name}
                        onChange={(e) =>
                          handleItemChange(index, "milestone_name", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Description (optional)"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeItem(index)}
                        title="Remove milestone"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="d-flex gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Creating...
                </>
              ) : (
                "Create Template"
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setFormData({
                  template_name: "",
                  description: "",
                  customer_id: "",
                  is_default: false,
                });
                setItems([{ milestone_name: "", description: "", sequence_order: 1 }]);
                setError("");
              }}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMilestoneTemplateForm;